import {
  CollapsibleContainer,
  ErrorBoundary,
  MapListActionRow,
  ProjectCard,
  SkeletonLoader,
  TableErrorDisplay,
  Text,
} from '@civilgrid/ui/components';
import { deriveFilterTypesFromColumns } from '@civilgrid/ui/hooks';
import { cn, getNonNullUserName } from '@civilgrid/ui/utils';
import { type MouseEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSnapshot } from 'valtio';
import type { OrganizationDetail, Project, TLoadedState } from '~/common/types';
import rootStore from '~/store';
import { isKeyboardActivation, mapListCardTestId, useFocusMapListCard, useHomeViewTableState } from '../hooks';
import { useGetProjectsInfinite, useGetSandboxProject, useToggleProjectBookmark } from '../queries';
import { homeMapStore, homeViewStore, setMapCount, setMapGeometryFilter, setMapLoading } from '../store';
import { formatAddress, getMapListFiltersForSection } from '../utils';
import { getProjectColumns } from './ProjectsTable/columns';

const MAP_LIST_PAGE_SIZE = 100;

type ProjectsMapListProps = {
  onProjectFocus?: (projectId: string) => void;
  className?: string;
};

/**
 * Sidebar list of projects for Map and Split views. Shares filters/globalFilter with table via homeViewStore.
 * Three sections, each with its own React Query: Bookmarked (bookmarkedOnly), My Projects (PM = me, excludeBookmarked),
 * All Projects (PM ≠ me, excludeBookmarked). When inOnboardingMode, the sandbox project is fetched
 * separately and prepended to the top of My Projects.
 */
export function ProjectsMapList({ onProjectFocus, className }: ProjectsMapListProps) {
  const { currentOrganizationId, currentOrganizationDetail, me, inOnboardingMode } = useSnapshot(
    rootStore,
  ) as typeof rootStore;
  const currentUserId = me?.id ?? null;

  const sandboxQuery = useGetSandboxProject(currentOrganizationId ?? '', inOnboardingMode);
  const sandboxProject = sandboxQuery.data ?? null;

  const orgMembers = useMemo(() => {
    if (currentOrganizationDetail.isLoading) return [];
    return (currentOrganizationDetail as TLoadedState & OrganizationDetail).members ?? [];
  }, [currentOrganizationDetail]);

  const filterOptions = useMemo(() => ({ orgMembers }), [orgMembers]);
  const handleShowOnMap = useCallback((project: Project) => {
    homeMapStore.setFlyToProject(project);
  }, []);
  const handleHoverOnProjectCard = useCallback((projectId?: string) => {
    homeMapStore.setHoverProjectId(projectId);
  }, []);
  const columns = useMemo(() => getProjectColumns(filterOptions), [filterOptions]);
  const filterTypes = useMemo(() => deriveFilterTypesFromColumns(columns), [columns]);
  const stateControl = useHomeViewTableState({ filterTypes });
  const { filters, globalFilter } = stateControl;
  const { mapGeometryFilter } = useSnapshot(homeViewStore);
  const { popupParams, hoverProjectId } = useSnapshot(homeMapStore);
  const toggleBookmark = useToggleProjectBookmark();
  const focusMapListCard = useFocusMapListCard();

  const handleBookmarkClick = useCallback(
    (projectId: string) => (e: MouseEvent) => {
      // The card moves to another section once the lists refetch, so keyboard focus follows it to the same button.
      const onSuccess = isKeyboardActivation(e) ? () => focusMapListCard(projectId, { target: 'bookmark' }) : undefined;
      toggleBookmark.mutate(projectId, { onSuccess });
    },
    [toggleBookmark, focusMapListCard],
  );

  const filtersBookmarked = useMemo(
    () => getMapListFiltersForSection(filters, 'bookmarked', currentUserId),
    [filters, currentUserId],
  );
  const filtersMy = useMemo(() => getMapListFiltersForSection(filters, 'my', currentUserId), [filters, currentUserId]);
  const filtersAll = useMemo(
    () => getMapListFiltersForSection(filters, 'all', currentUserId),
    [filters, currentUserId],
  );

  const bookmarkedQuery = useGetProjectsInfinite({
    organizationId: currentOrganizationId,
    pageSize: MAP_LIST_PAGE_SIZE,
    filters: filtersBookmarked,
    globalFilter,
    bookmarkedOnly: true,
    geometryFilter: mapGeometryFilter ? (mapGeometryFilter as import('geojson').MultiPolygon) : undefined,
    enabled: !!currentOrganizationId,
  });
  const myQuery = useGetProjectsInfinite({
    organizationId: currentOrganizationId,
    pageSize: MAP_LIST_PAGE_SIZE,
    filters: filtersMy,
    globalFilter,
    excludeBookmarked: true,
    geometryFilter: mapGeometryFilter ? (mapGeometryFilter as import('geojson').MultiPolygon) : undefined,
    enabled: !!currentOrganizationId,
  });
  const allQuery = useGetProjectsInfinite({
    organizationId: currentOrganizationId,
    pageSize: MAP_LIST_PAGE_SIZE,
    filters: filtersAll,
    globalFilter,
    excludeBookmarked: true,
    geometryFilter: mapGeometryFilter ? (mapGeometryFilter as import('geojson').MultiPolygon) : undefined,
    enabled: !!currentOrganizationId,
  });

  const bookmarkedProjects = useMemo(
    () => bookmarkedQuery.data?.pages?.flatMap((p) => p.projects) ?? [],
    [bookmarkedQuery.data?.pages],
  );
  const myProjectsFromQuery = useMemo(
    () => myQuery.data?.pages?.flatMap((p) => p.projects) ?? [],
    [myQuery.data?.pages],
  );
  const allProjects = useMemo(() => allQuery.data?.pages?.flatMap((p) => p.projects) ?? [], [allQuery.data?.pages]);
  const myProjects = useMemo(() => {
    if (inOnboardingMode && sandboxProject) {
      const rest = myProjectsFromQuery.filter((p) => p.id !== sandboxProject.id);
      return [sandboxProject, ...rest];
    }
    return myProjectsFromQuery;
  }, [inOnboardingMode, sandboxProject, myProjectsFromQuery]);

  const bookmarkedCount = bookmarkedQuery.data?.pages?.[0]?.count ?? 0;
  const myCount = useMemo(() => {
    const count = myQuery.data?.pages?.[0]?.count ?? 0;
    if (inOnboardingMode && sandboxProject) {
      const alreadyInResults = myProjectsFromQuery.some((p) => p.id === sandboxProject.id);
      return alreadyInResults ? count : count + 1;
    }
    return count;
  }, [inOnboardingMode, sandboxProject, myProjectsFromQuery, myQuery.data?.pages]);
  const allCount = allQuery.data?.pages?.[0]?.count ?? 0;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreBookmarkedRef = useRef<HTMLDivElement>(null);
  const loadMoreMyRef = useRef<HTMLDivElement>(null);
  const loadMoreAllRef = useRef<HTMLDivElement>(null);

  const isLoading = bookmarkedQuery.isLoading || myQuery.isLoading || allQuery.isLoading;
  const isFetchingNext =
    bookmarkedQuery.isFetchingNextPage || myQuery.isFetchingNextPage || allQuery.isFetchingNextPage;
  const error = bookmarkedQuery.error ?? myQuery.error ?? allQuery.error;

  useEffect(() => {
    setMapCount(bookmarkedCount + myCount + allCount);
  }, [bookmarkedCount, myCount, allCount]);

  useEffect(() => {
    setMapLoading(isLoading || isFetchingNext);
  }, [isLoading, isFetchingNext]);

  useEffect(() => {
    const sentinel = loadMoreBookmarkedRef.current;
    const root = scrollContainerRef.current;
    if (
      !sentinel ||
      !root ||
      !bookmarkedQuery.hasNextPage ||
      bookmarkedQuery.isLoading ||
      bookmarkedQuery.isFetchingNextPage
    )
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) bookmarkedQuery.fetchNextPage();
      },
      { root, rootMargin: '200px 0px', threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    bookmarkedQuery.hasNextPage,
    bookmarkedQuery.isLoading,
    bookmarkedQuery.isFetchingNextPage,
    bookmarkedQuery.fetchNextPage,
  ]);

  useEffect(() => {
    const sentinel = loadMoreMyRef.current;
    const root = scrollContainerRef.current;
    if (!sentinel || !root || !myQuery.hasNextPage || myQuery.isLoading || myQuery.isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) myQuery.fetchNextPage();
      },
      { root, rootMargin: '200px 0px', threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [myQuery.hasNextPage, myQuery.isLoading, myQuery.isFetchingNextPage, myQuery.fetchNextPage]);

  useEffect(() => {
    const sentinel = loadMoreAllRef.current;
    const root = scrollContainerRef.current;
    if (!sentinel || !root || !allQuery.hasNextPage || allQuery.isLoading || allQuery.isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) allQuery.fetchNextPage();
      },
      { root, rootMargin: '200px 0px', threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [allQuery.hasNextPage, allQuery.isLoading, allQuery.isFetchingNextPage, allQuery.fetchNextPage]);

  if (!currentOrganizationId) return null;

  const hasAnyProjects = bookmarkedProjects.length > 0 || myProjects.length > 0 || allProjects.length > 0;

  return (
    <div
      id="projects-list"
      data-testid="home-projects-map-list"
      className={cn('flex h-full flex-col gap-2', className)}
    >
      <ErrorBoundary
        fallback={(err) => <TableErrorDisplay error={err} />}
        onError={(e, info) => console.error('ProjectsMapList error:', e, info)}
      >
        <div className="shrink-0">
          <MapListActionRow
            filterTypes={filterTypes}
            stateControl={stateControl}
            customFilterPills={
              mapGeometryFilter
                ? [
                    {
                      id: 'mapGeometry',
                      label: 'Map filtering Table',
                      onRemove: () => setMapGeometryFilter(null),
                      labelOnly: true,
                    },
                  ]
                : []
            }
          />
        </div>
        <div className="flex-1 overflow-y-hidden flex border border-grey-30 rounded-md">
          <div
            ref={scrollContainerRef}
            data-map-list-scroll
            className={cn(
              'h-full w-full overflow-y-auto',
              '[&::-webkit-scrollbar]:w-2.5',
              '[&::-webkit-scrollbar]:h-2.5',
              '[&::-webkit-scrollbar-thumb]:bg-grey-30 [&::-webkit-scrollbar-thumb:hover]:bg-grey-50 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border [&::-webkit-scrollbar-thumb]:border-white',
              '[&::-webkit-scrollbar-track]:border [&::-webkit-scrollbar-track]:border-white [&::-webkit-scrollbar-track]:bg-transparent',
            )}
          >
            {isLoading ? (
              <div className="flex flex-col gap-0">
                {Array.from({ length: 6 }).map((_, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: legacy
                  <div key={i} className="flex flex-1 flex-col gap-0.5 bg-transparent p-2">
                    <SkeletonLoader width={180} height={14} className="mb-0.5" />
                    <SkeletonLoader width={120} height={12} className="mb-0.5" />
                    <SkeletonLoader width={150} height={12} />
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="flex items-center justify-center p-8">
                <Text className="text-grey-60">Error loading projects</Text>
              </div>
            ) : !hasAnyProjects ? (
              <div className="flex items-center justify-center p-8">
                <Text className="text-grey-60">No projects found</Text>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5 p-2">
                {bookmarkedCount > 0 && (
                  <CollapsibleContainer label="Bookmarked" count={bookmarkedCount} defaultExpanded>
                    {bookmarkedProjects.map((p) => (
                      <ProjectCard
                        key={p.id}
                        name={p.name}
                        billingTier={p.billingTier}
                        pmName={getNonNullUserName(p.projectManager?.name) || p.projectManager?.email || ''}
                        projectId={p.projectId || undefined}
                        address={p.address2 ? formatAddress(p.address2) : undefined}
                        linkProps={{
                          to: `/projects/${p.id}`,
                          'data-testid': mapListCardTestId(p.id),
                        }}
                        onFocus={() => onProjectFocus?.(p.id)}
                        onHover={handleHoverOnProjectCard}
                        isHover={p.projectId === hoverProjectId}
                        bookmarked={p.bookmarked}
                        onBookmarkClick={handleBookmarkClick(p.id)}
                        onFlyToClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleShowOnMap(p);
                        }}
                        isSandboxProject={p.sandboxProject}
                        selected={popupParams?.projectId === p.projectId}
                      />
                    ))}
                    {bookmarkedQuery.hasNextPage && (
                      <div ref={loadMoreBookmarkedRef} className="min-h-px" aria-hidden />
                    )}
                    {bookmarkedQuery.isFetchingNextPage && (
                      <div className="flex justify-center py-2">
                        <SkeletonLoader width={120} height={12} />
                      </div>
                    )}
                  </CollapsibleContainer>
                )}

                <CollapsibleContainer label="My Projects" count={myCount} defaultExpanded>
                  {myProjects.map((p) => (
                    <ProjectCard
                      key={p.id}
                      name={p.name}
                      billingTier={p.billingTier}
                      pmName={p.projectManager?.name ?? p.projectManager?.email ?? undefined}
                      projectId={p.projectId || undefined}
                      address={p.address2 ? formatAddress(p.address2) : undefined}
                      linkProps={{
                        to: `/projects/${p.id}`,
                        'data-testid': mapListCardTestId(p.id),
                      }}
                      onFocus={() => onProjectFocus?.(p.id)}
                      onHover={handleHoverOnProjectCard}
                      isHover={p.projectId === hoverProjectId}
                      bookmarked={p.bookmarked}
                      onBookmarkClick={handleBookmarkClick(p.id)}
                      onFlyToClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleShowOnMap(p);
                      }}
                      isSandboxProject={p.sandboxProject}
                      selected={popupParams?.projectId === p.projectId}
                    />
                  ))}
                  {myQuery.hasNextPage && <div ref={loadMoreMyRef} className="min-h-px" aria-hidden />}
                  {myQuery.isFetchingNextPage && (
                    <div className="flex justify-center py-2">
                      <SkeletonLoader width={120} height={12} />
                    </div>
                  )}
                </CollapsibleContainer>

                <CollapsibleContainer label="All Projects" count={allCount} defaultExpanded>
                  {allProjects.map((p) => (
                    <ProjectCard
                      key={p.id}
                      name={p.name}
                      billingTier={p.billingTier}
                      pmName={p.projectManager?.name ?? p.projectManager?.email ?? undefined}
                      projectId={p.projectId || undefined}
                      address={p.address2 ? formatAddress(p.address2) : undefined}
                      linkProps={{
                        to: `/projects/${p.id}`,
                        'data-testid': mapListCardTestId(p.id),
                      }}
                      onFocus={() => onProjectFocus?.(p.id)}
                      onHover={handleHoverOnProjectCard}
                      isHover={p.projectId === hoverProjectId}
                      bookmarked={p.bookmarked}
                      onBookmarkClick={handleBookmarkClick(p.id)}
                      onFlyToClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleShowOnMap(p);
                      }}
                      isSandboxProject={p.sandboxProject}
                      selected={popupParams?.projectId === p.projectId}
                    />
                  ))}
                  {allQuery.hasNextPage && <div ref={loadMoreAllRef} className="min-h-px" aria-hidden />}
                  {allQuery.isFetchingNextPage && (
                    <div className="flex justify-center py-2">
                      <SkeletonLoader width={120} height={12} />
                    </div>
                  )}
                </CollapsibleContainer>
              </div>
            )}
          </div>
        </div>
      </ErrorBoundary>
    </div>
  );
}
